<script setup lang="ts" generic="T, C extends ColumnModel, R extends RecordMap<C>">
import { PV } from "@/widgets";
import { ref } from "vue";
import type { ColumnModel, RecordMap, TableColumn, defineTable } from "../common";
import { CustomTable } from "../internal";

const props = defineProps<{
    table: ReturnType<typeof defineTable<T, C, R>>;
    expandable?: boolean;
}>();

const slots = defineSlots<
    TableColumn<R> & {
        _tools: () => any;
        _buttons: () => any;
    }
>();

const search = ref<string>();

function castName(name: string) {
    return name as keyof typeof slots;
}
</script>

<template>
    <CustomTable v-model:search="search" :columns="props.table.columns" :table="props.table">
        <template #headers="{ refresh }">
            <div class="flex flex-col md:flex-row gap-2 md:gap-0 items-center justify-center py-1">
                <div class="w-auto md:w-3/12 flex flex-row justify-start">
                    <PV.Button icon="pi pi-refresh" severity="secondary" size="small" text @click="refresh" />
                    <slot name="_tools"></slot>
                </div>

                <div class="grow w-full md:w-auto">
                    <PV.IconField class="grow">
                        <PV.InputIcon class="pi pi-search" />
                        <PV.InputText v-model.trim="search" fluid placeholder="Cari" size="small" />
                        <PV.InputIcon
                            v-if="search !== undefined && search.trim().length > 0"
                            class="pi pi-times hover:cursor-pointer"
                            @click="() => (search = undefined)"
                        />
                    </PV.IconField>
                </div>

                <div class="w-auto xl:w-3/12 flex flex-row justify-end">
                    <slot name="_buttons"></slot>
                </div>
            </div>
        </template>

        <template v-for="column in props.table.columns">
            <PV.Column :field="column.field" :header="column.title">
                <template v-if="slots[castName(column.field)] !== undefined" #body="{ data }">
                    <slot :data="data" :name="castName(column.field)" />
                </template>
            </PV.Column>
        </template>
    </CustomTable>
</template>
